import { ModalPresence } from '../components/ModalPresence';
import { useContentEntrance } from '../components/Motion';
import { VerificationCodeInput } from '../components/VerificationCodeInput';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Download, KeyRound, Laptop, LogOut, ShieldCheck, Smartphone, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, downloadApiFile } from '../api';
import { useAuth } from '../auth';
import { AvatarCropper, type CropRect } from '../components/AvatarCropper';
import { useToast } from '../toast';

interface Session {
  id: string;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  is_current: boolean;
}

function deviceLabel(agent: string | null) {
  if (!agent) return 'Unknown device';
  const browser = agent.includes('Edg/') ? 'Edge' : agent.includes('Firefox/') ? 'Firefox' : agent.includes('Chrome/') ? 'Chrome' : agent.includes('Safari/') ? 'Safari' : 'Browser';
  const system = agent.includes('Windows') ? 'Windows' : agent.includes('Mac OS') ? 'macOS' : agent.includes('Android') ? 'Android' : agent.includes('iPhone') || agent.includes('iPad') ? 'iOS' : agent.includes('Linux') ? 'Linux' : 'Unknown OS';
  return `${browser} on ${system}`;
}

export function AccountSettings() {
  const { user, refresh } = useAuth();
  const client = useQueryClient();
  const navigate = useNavigate();
  const notify = useToast();
  const [section, setSection] = useState<'profile' | 'security' | 'sessions' | 'data'>('profile');
  const sectionRef = useRef<HTMLDivElement>(null);
  useContentEntrance(sectionRef, section);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string }>();
  const [totpSetup, setTotpSetup] = useState<{ secret: string; otpauth_url: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [deleteError,setDeleteError]=useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteState, setDeleteState] = useState<{
    emailed: boolean;
    totp_required: boolean;
    retry_after_ms: number;
  } | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const photoInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    setCooldownUntil(Date.now() + (user?.avatar_retry_after_ms ?? 0));
  }, [user?.avatar_retry_after_ms, user?.avatar_url]);
  const photoRemaining = Math.max(0, cooldownUntil - now);
  const formatWait = (ms: number) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
  };
  const savePhoto = async (crop: CropRect) => {
    if (!photoFile) return;
    setPhotoBusy(true);
    try {
      const body = new FormData();
      body.set('photo', photoFile);
      body.set('crop', JSON.stringify(crop));
      const result = await api<{ avatar: { url: string }; avatar_retry_after_ms: number }>('/auth/avatar', {
        method: 'POST',
        body,
      });
      setPhotoFile(null);
      setCooldownUntil(Date.now() + (result.avatar_retry_after_ms ?? 60 * 60 * 1000));
      await refresh();
      setNotice({ tone: 'success', text: 'Profile photo updated.' });
    } catch (error: any) {
      setNotice({ tone: 'error', text: error.message });
    } finally {
      setPhotoBusy(false);
    }
  };
  const sessions = useQuery({
    queryKey: ['account-sessions'],
    queryFn: () => api<{ sessions: Session[] }>('/auth/sessions').then((result) => result.sessions),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api<void>(`/auth/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['account-sessions'] }),
  });
  const revokeOthers = useMutation({
    mutationFn: () => api<void>('/auth/sessions/revoke-others', { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['account-sessions'] }),
  });

  const saveProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await api('/auth/profile', { method: 'PATCH', body: JSON.stringify({ display_name: form.get('display_name') }) });
      await refresh();
      setNotice({ tone: 'success', text: 'Profile updated.' });
    } catch (error: any) {
      setNotice({ tone: 'error', text: error.message });
    }
  };

  const changePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      await api('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: form.get('current_password'), new_password: form.get('new_password') }),
      });
      formElement.reset();
      await sessions.refetch();
      setNotice({ tone: 'success', text: 'Password changed. Other sessions were signed out.' });
    } catch (error: any) {
      setNotice({ tone: 'error', text: error.message });
    }
  };

  const startTotp = async () => {
    try {
      const result = await api<{ secret: string; otpauth_url: string }>('/auth/two-factor/setup', {
        method: 'POST',
      });
      setTotpSetup(result);
      setNotice({ tone: 'success', text: 'Add this secret to your authenticator, then confirm a code.' });
    } catch (error: any) {
      setNotice({ tone: 'error', text: error.message });
    }
  };

  const enableTotp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ recovery_codes: string[] }>('/auth/two-factor/enable', {
        method: 'POST',
        body: JSON.stringify({ code: form.get('code') }),
      });
      setTotpSetup(null);
      setRecoveryCodes(result.recovery_codes);
      await refresh();
      setNotice({ tone: 'success', text: 'Two-factor authentication is on. Store the recovery codes.' });
    } catch (error: any) {
      setNotice({ tone: 'error', text: error.message });
    }
  };

  const disableTotp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await api('/auth/two-factor/disable', {
        method: 'POST',
        body: JSON.stringify({ code: form.get('code') }),
      });
      setRecoveryCodes(null);
      await refresh();
      setNotice({ tone: 'success', text: 'Two-factor authentication is off.' });
    } catch (error: any) {
      setNotice({ tone: 'error', text: error.message });
    }
  };

  const startDeletion = async () => {
    setDeleteOpen(true);
    setDeleteState(null);
    setDeleteBusy(true);
    setDeleteError('');
    try {
      const result = await api<{ totp_required: boolean; mailed?: boolean; retry_after_ms: number }>(
        '/auth/delete-account',
        { method: 'POST' },
      );
      setDeleteState({
        emailed: result.mailed !== false,
        totp_required: result.totp_required,
        retry_after_ms: result.retry_after_ms,
      });
      setDeleteOpen(true);
    } catch (error: any) {
      setDeleteError(error.message);
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmDeletion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await api('/auth/delete-account/confirm', {
        method: 'POST',
        body: JSON.stringify({
          code: form.get('code'),
          totp_code: form.get('totp_code') || undefined,
        }),
      });
      setDeleteOpen(false);
      client.clear();
      navigate('/login', { state: { deletionRequested: true } });
    } catch (error: any) {
      setDeleteError(error.message);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="dash-page settings-page">
      <header className="dash-page__heading">
        <div><p>Account</p><h1>Settings</h1><span>Manage your profile, sign-in security, and account data.</span></div>
      </header>
      {notice && <div className={`settings-notice settings-notice--${notice.tone}`} role="status"><Check />{notice.text}</div>}
      <nav className="dashboard-section-nav" aria-label="Account sections">
        {(['profile', 'security', 'sessions', 'data'] as const).map((item) => (
          <button key={item} type="button" aria-current={section === item ? 'page' : undefined} onClick={() => setSection(item)}>
            {{ profile: 'Profile', security: 'Security', sessions: 'Sessions', data: 'Your data' }[item]}
          </button>
        ))}
      </nav>
      <div ref={sectionRef} className="settings-layout account-settings-layout">
        <section className="settings-section" hidden={section !== 'profile'}>
          <div className="settings-section__intro"><ShieldCheck /><div><h2>Profile</h2><p>This name appears throughout your workspace and administrative records.</p></div></div>
          <form className="settings-form" onSubmit={saveProfile}>
            <div className="profile-photo">
              <span className="profile-photo__preview">
                {user?.avatar_url ? <img src={user.avatar_url} alt="" /> : user?.display_name?.slice(0, 1).toUpperCase()}
              </span>
              <div>
                <strong>Profile photo</strong>
                <p>JPEG, PNG, WebP or GIF, up to 10 MB. Saved as a 512px square. Motion is kept as an animated WebP.</p>
                <input
                  ref={photoInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.currentTarget.value = '';
                    if (file) setPhotoFile(file);
                  }}
                />
                <button
                  className="button button--dash-quiet"
                  type="button"
                  disabled={photoRemaining > 0}
                  onClick={() => photoInput.current?.click()}
                >
                  {photoRemaining > 0 ? `Change photo in ${formatWait(photoRemaining)}` : 'Change photo'}
                </button>
              </div>
            </div>
            <label><span>Display name</span><input name="display_name" pattern=".*\S.*" title="Enter a name containing visible characters." defaultValue={user?.display_name} minLength={2} maxLength={80} required /></label>
            <label><span>Email</span><input value={user?.email ?? ''} disabled /><small>Email changes require verification and are not available yet.</small></label>
            <button className="button button--dash-quiet">Save profile</button>
          </form>
        </section>
        <section className="settings-section" hidden={section !== 'security'}>
          <div className="settings-section__intro"><KeyRound /><div><h2>Password</h2><p>Changing it immediately revokes every other active session.</p></div></div>
          {user?.has_password === false ? (
            <p className="empty-copy">This account signs in with Discord and does not have a password. Use Forgot password only after a password has been set.</p>
          ) : (
            <form className="settings-form" onSubmit={changePassword}>
              <label><span>Current password</span><input name="current_password" maxLength={128} type="password" autoComplete="current-password" required /></label>
              <label><span>New password</span><input name="new_password" pattern=".*\S.*" title="Use 10–128 characters, not only spaces." type="password" autoComplete="new-password" minLength={10} maxLength={128} required /><small>Use at least 10 characters and a password you do not reuse.</small></label>
              <button className="button button--light">Change password</button>
            </form>
          )}
        </section>
        <section className="settings-section" hidden={section !== 'security'}>
          <div className="settings-section__intro">
            <ShieldCheck />
            <div>
              <h2>Two-factor authentication</h2>
              <p>Password sign-in then asks for an authenticator code. Discord sign-in uses the same prompt when 2FA is on.</p>
            </div>
          </div>
          {user?.totp_enabled ? (
            <form className="settings-form" onSubmit={disableTotp}>
              <label>
                <span>Authenticator or recovery code</span>
                <VerificationCodeInput name="code" recovery required />
              </label>
              <button className="button button--quiet">Turn off 2FA</button>
            </form>
          ) : totpSetup ? (
            <form className="settings-form" onSubmit={enableTotp}>
              <label>
                <span>Authenticator secret</span>
                <input value={totpSetup.secret} readOnly onFocus={(event) => event.currentTarget.select()} />
                <small>Add a TOTP account in your authenticator, then paste this secret. Confirmation uses a 6-digit code from that app.</small>
              </label>
              <label>
                <span>Confirmation code</span>
                <VerificationCodeInput name="code" required />
              </label>
              <button className="button button--light">Enable 2FA</button>
            </form>
          ) : (
            <div className="settings-form">
              <button className="button button--dash-quiet" type="button" onClick={() => void startTotp()}>
                Set up authenticator
              </button>
            </div>
          )}
          {recoveryCodes && (
            <div className="recovery-codes">
              <p>Store these recovery codes. Each one works once.</p>
              <ol>
                {recoveryCodes.map((code) => (
                  <li key={code}>
                    <code>{code}</code>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>
        <section className="settings-section" hidden={section !== 'data'}>
          <div className="settings-section__intro"><Download /><div><h2>Your data</h2><p>Export your account record as a JSON file.</p></div></div>
          <div className="settings-form">
            <button
              className="button button--dash-quiet"
              type="button"
              onClick={() => {
                void downloadApiFile('/auth/export', `bckt-account-${user?.id ?? 'export'}.json`).then(
                  () => notify({ title: 'Account export downloaded', tone: 'success' }),
                  (error: unknown) =>
                    notify({
                      title: 'Account export failed',
                      description:
                        error instanceof Error ? error.message : 'The account export could not be downloaded.',
                      tone: 'error',
                    }),
                );
              }}
            >
              Download account export
            </button>
          </div>
        </section>
        <section className="settings-section settings-section--danger" hidden={section !== 'data'}>
          <div className="settings-section__intro"><Trash2 /><div><h2>Delete account</h2><p>Access is revoked immediately. We then delete your files and logs and cancel your subscription without a refund. Failed cleanup steps are retried. We send one final email when complete. This cannot be undone.</p></div></div>
          {user?.workspace?.role === 'owner' ? (
            <div className="settings-form">
              <button className="button button--danger" type="button" disabled={deleteBusy} onClick={() => void startDeletion()}>
                {deleteBusy && !deleteOpen ? 'Sending code…' : 'Delete this account'}
              </button>
            </div>
          ) : (
            <p className="empty-copy">Only the workspace owner can delete this account.</p>
          )}
        </section>
        <section className="settings-section settings-section--sessions" hidden={section !== 'sessions'}>
          <div className="settings-section__intro"><Laptop /><div><h2>Active sessions</h2><p>Sessions remain active for up to 90 days and renew while you use BCKT.</p></div><button className="button button--quiet" onClick={() => revokeOthers.mutate()} disabled={revokeOthers.isPending}>Sign out others</button></div>
          {sessions.isLoading ? <div className="settings-session-loading" /> : sessions.isError ? <p className="form-error">Sessions could not be loaded.</p> : (
            <div className="session-list">
              {sessions.data?.map((session) => (
                <article key={session.id}>
                  <span className="session-device">{session.user_agent?.match(/Mobile|Android|iPhone/) ? <Smartphone /> : <Laptop />}</span>
                  <div><strong>{deviceLabel(session.user_agent)}</strong><p>{session.ip || 'IP unavailable'}<span>Last active {new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(session.last_seen_at))}</span></p><small>{session.is_current ? 'This device' : `Started ${new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(session.created_at))}`}</small></div>
                  <button className="icon-button" aria-label={session.is_current ? 'Sign out this session' : 'Revoke session'} onClick={() => revoke.mutate(session.id)}><LogOut /></button>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
      {photoFile ? (
        <AvatarCropper
          file={photoFile}
          busy={photoBusy}
          onCancel={() => setPhotoFile(null)}
          onConfirm={(crop) => void savePhoto(crop)}
        />
      ) : null}
      <ModalPresence>{deleteOpen ? (
        <div className="secret-modal" role="alertdialog" aria-modal="true">
          <button className="secret-modal__scrim" aria-label="Close deletion dialog" disabled={deleteBusy} onClick={() => setDeleteOpen(false)} />
          <form className="revoke-dialog" onSubmit={confirmDeletion}>
            <span><Trash2 /></span>
            <h2>Delete this account now?</h2>
            <p>This cannot be undone. Your access is revoked immediately. Files, logs and subscription cancellation are processed in the background, with retries if needed. You will receive one final confirmation email when complete. Child-safety evidence preserved for legal reporting is not destroyed.</p>
            {deleteError&&<p className="form-error deletion-error" role="alert">{deleteError}</p>}
            {!deleteState&&<p role="status">{deleteBusy?'Sending your confirmation code…':'A confirmation code is required before deleting your account.'}</p>}
            {deleteState&&<label>
              <span>Email code</span>
              <VerificationCodeInput name="code" required autoComplete="one-time-code" />
            </label>}
            {deleteState?.totp_required ? (
              <label>
                <span>Authenticator or recovery code</span>
                <VerificationCodeInput name="totp_code" recovery required />
              </label>
            ) : null}
            <div className="dialog-actions">
              <button type="button" className="button button--dash-quiet" onClick={() => setDeleteOpen(false)} disabled={deleteBusy}>
                Cancel
              </button>
              <button type={deleteState?'submit':'button'} onClick={!deleteState?()=>void startDeletion():undefined} className="button button--danger" disabled={deleteBusy}>
                {deleteBusy ? 'Please wait…' : deleteState ? 'Delete permanently' : 'Retry sending code'}
              </button>
            </div>
          </form>
        </div>
      ) : null}</ModalPresence>
    </div>
  );
}
